/**
 * NewLeadDialog — creazione manuale di un'azienda nel CRM di piattaforma.
 *
 * Campi obbligatori: ragione sociale + partita IVA (validazione formato
 * italiano con cifra di controllo). Dedupe sulla P.IVA: se esiste già un
 * record con la stessa P.IVA la creazione è bloccata e viene offerto il
 * link al record esistente.
 */

import React, { useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, Switch,
  TextField, Typography,
} from '@mui/material';
import { AddBusiness as AddBusinessIcon, OpenInNew } from '@mui/icons-material';
import { tokens } from '../../theme';
import {
  createPlatformLead, findLeadByPartitaIva, validatePartitaIva,
} from '../../services/platformCrmService';
import type { PlatformLead, PlatformLeadFonte } from '../../types/platformCrm';
import { FONTE_LABELS, FONTE_ORDER } from './labels';

interface FormState {
  ragione_sociale: string;
  partita_iva: string;
  email: string;
  telefono: string;
  citta: string;
  provincia: string;
  settore: string;
  fonte: PlatformLeadFonte;
  partner_segnalatore: string;
  founding_partner: boolean;
  note: string;
}

const EMPTY_FORM: FormState = {
  ragione_sociale: '', partita_iva: '', email: '', telefono: '',
  citta: '', provincia: '', settore: '', fonte: 'manuale',
  partner_segnalatore: '', founding_partner: false, note: '',
};

interface Props {
  open: boolean;
  onClose: () => void;
  /** Chiamata dopo la creazione riuscita (id del nuovo lead). */
  onCreated: (id: string) => void;
  /** Apre il dettaglio di un record esistente (caso dedupe P.IVA). */
  onOpenExisting: (lead: PlatformLead) => void;
  /**
   * Partner segnalatori già presenti nei lead caricati: alimentano
   * l'autocomplete per coerenza dei nomi, senza hardcode.
   */
  partnerOptions: string[];
}

export const NewLeadDialog: React.FC<Props> = ({
  open, onClose, onCreated, onOpenExisting, partnerOptions,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<PlatformLead | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((s) => ({ ...s, [key]: value }));
    setError(null);
    setDuplicate(null);
  };

  const handleClose = () => {
    if (submitting) return;
    setForm(EMPTY_FORM);
    setError(null);
    setDuplicate(null);
    onClose();
  };

  const handleSubmit = async () => {
    const ragioneSociale = form.ragione_sociale.trim();
    if (!ragioneSociale) {
      setError('La ragione sociale è obbligatoria');
      return;
    }
    const piva = validatePartitaIva(form.partita_iva);
    if (!piva.ok) {
      setError(`Partita IVA: ${piva.error}`);
      return;
    }
    setSubmitting(true);
    setError(null);
    setDuplicate(null);
    try {
      const existing = await findLeadByPartitaIva(piva.normalized);
      if (existing) {
        setDuplicate(existing);
        return;
      }
      const id = await createPlatformLead({
        ragione_sociale: ragioneSociale,
        partita_iva: piva.normalized,
        email: form.email.trim(),
        telefono: form.telefono.trim(),
        citta: form.citta.trim(),
        provincia: form.provincia.trim().toUpperCase(),
        settore: form.settore.trim(),
        fonte: form.fonte,
        stato: 'nuovo',
        partner_segnalatore: form.partner_segnalatore.trim(),
        founding_partner: form.founding_partner,
        note: form.note.trim(),
      });
      setForm(EMPTY_FORM);
      onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore durante la creazione');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        fontFamily: tokens.font.display, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em',
      }}>
        <AddBusinessIcon sx={{ color: tokens.color.tierBusiness.main }} />
        Nuova azienda
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.color.ash, mb: 2 }}>
          Censisci un'azienda nel funnel di acquisizione. La partita IVA è la chiave
          univoca: se esiste già un record verrà segnalato.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Ragione sociale *"
            value={form.ragione_sociale}
            onChange={(e) => set('ragione_sociale', e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label="Partita IVA *"
            value={form.partita_iva}
            onChange={(e) => set('partita_iva', e.target.value)}
            fullWidth
            placeholder="11 cifre (prefisso IT opzionale)"
            InputProps={{ sx: { fontFamily: tokens.font.mono } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              fullWidth
            />
            <TextField
              label="Telefono"
              value={form.telefono}
              onChange={(e) => set('telefono', e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Città"
              value={form.citta}
              onChange={(e) => set('citta', e.target.value)}
              fullWidth
            />
            <TextField
              label="Provincia"
              value={form.provincia}
              onChange={(e) => set('provincia', e.target.value)}
              sx={{ width: { sm: 140 } }}
              inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
              placeholder="es. CA"
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Settore"
              value={form.settore}
              onChange={(e) => set('settore', e.target.value)}
              fullWidth
              placeholder="es. fotovoltaico, infissi"
            />
            <TextField
              select
              label="Fonte"
              value={form.fonte}
              onChange={(e) => set('fonte', e.target.value as PlatformLeadFonte)}
              sx={{ minWidth: { sm: 220 } }}
            >
              {FONTE_ORDER.map((f) => (
                <MenuItem key={f} value={f}>{FONTE_LABELS[f]}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Autocomplete
              freeSolo
              options={partnerOptions}
              inputValue={form.partner_segnalatore}
              onInputChange={(_, value) => set('partner_segnalatore', value)}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Partner segnalatore"
                  placeholder="es. Forniture Fotovoltaiche"
                  inputProps={{ ...params.inputProps, maxLength: 80 }}
                />
              )}
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={form.founding_partner}
                  onChange={(e) => set('founding_partner', e.target.checked)}
                />
              )}
              label={<Typography variant="body2">Founding partner</Typography>}
              sx={{ flexShrink: 0, mr: 0 }}
            />
          </Stack>
          <TextField
            label="Note"
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>

        {duplicate && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<OpenInNew fontSize="small" />}
                onClick={() => {
                  handleClose();
                  onOpenExisting(duplicate);
                }}
              >
                Apri il record
              </Button>
            }
          >
            Esiste già un'azienda con questa P.IVA:{' '}
            <Box component="strong">{duplicate.ragione_sociale}</Box>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Annulla</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <AddBusinessIcon />}
        >
          {submitting ? 'Creazione...' : 'Crea azienda'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
